import { Head, useForm } from "@inertiajs/react";
import { Lock } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Notice } from "@/components/ui/notice";
import { Textarea } from "@/components/ui/textarea";
import FormField from "@/components/forms/FormField";
import MoneyField from "@/components/forms/MoneyField";
import CampaignWindowFields from "@/components/schools/CampaignWindowFields";
import { WINDOW_DEFAULTS } from "@/lib/campaignWindows";
import { keyDatesFor } from "@/lib/campaignPlanning";
import FormSection from "@/components/forms/FormSection";
import ContactLink from "@/components/ContactLink";

export default function EditCampaign({ school, membership, campaign, update_path, back, errors: serverErrors = {}, values = {} }) {
  // Flat fields: CampaignsController's wrap_parameters nests them under
  // :campaign (including money_goal_dollars).
  const { data, setData, patch, processing, errors: inertiaErrors } = useForm({
    name: values.name || "",
    start_at: values.start_at || "",
    end_at: values.end_at || "",
    minute_goal: values.minute_goal || "",
    money_goal_dollars: values.money_goal_dollars || "",
    description: values.description || "",
    donation_lead_days: "donation_lead_days" in values ? values.donation_lead_days : WINDOW_DEFAULTS.donation_lead_days,
    minute_logging_days: values.minute_logging_days ?? WINDOW_DEFAULTS.minute_logging_days,
    donation_days_after: values.donation_days_after ?? WINDOW_DEFAULTS.donation_days_after,
  });

  const errors = { ...serverErrors, ...inertiaErrors };
  const datesLocked = Boolean(campaign?.dates_locked);

  const submit = (e) => {
    e.preventDefault();
    patch(update_path);
  };

  return (
    <SchoolLayout school={school} membership={membership} title={campaign?.name || "Edit campaign"} back={back}>
      <Head title={`Edit ${campaign?.name || "campaign"}`} />

      <div>
        <Card>
          <CardHeader>
            <CardTitle>Campaign details</CardTitle>
            <CardDescription>The name, dates, goals and what you're raising money for.</CardDescription>
          </CardHeader>
          <CardContent>
            {errors.base && <Notice tone="destructive" className="mb-6">{errors.base}</Notice>}

            <form onSubmit={submit} className="space-y-8">
              <div className="grid gap-6 lg:grid-cols-2">
                <FormField
                  name="name"
                  label="Campaign name"
                  required
                  placeholder="Spring Read-a-thon 2026"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  error={errors.name || errors.slug}
                  help="Families see it on every page, sheet and receipt."
                />

                <FormField
                  name="description"
                  label="What you're raising money for"
                  error={errors.description}
                  help="Shown on donation pages. You can also add it to the info sheets."
                >
                  <Textarea
                    id="description"
                    name="description"
                    value={data.description}
                    onChange={(e) => setData("description", e.target.value)}
                    placeholder="We're raising money for new library books while spending two weeks reading together."
                  />
                </FormField>
              </div>

              <section className="space-y-5">
                <h3 className="font-display text-xl font-semibold text-foreground">Dates</h3>
                <div className="grid gap-5 md:grid-cols-2">
                  <DateField name="start_at" label="Reading starts" value={data.start_at} onChange={(v) => setData("start_at", v)} error={errors.start_at} locked={datesLocked} />
                  <DateField name="end_at" label="Last day of reading" value={data.end_at} onChange={(v) => setData("end_at", v)} error={errors.end_at} locked={datesLocked} />
                </div>
                <CampaignWindowFields
                  data={data}
                  setData={setData}
                  errors={errors}
                  locked={datesLocked}
                  today={campaign.today}
                  dates={data.start_at && data.end_at && data.end_at >= data.start_at
                    ? keyDatesFor(data.start_at, data.end_at, data, campaign.today, data.donation_lead_days === null ? campaign.saved_donations_open_on : null)
                    : null}
                />
                {datesLocked && (
                  <p className="text-sm text-muted-foreground">
                    This campaign has started, so its dates are fixed. If they need to change, <ContactLink className="font-medium text-primary hover:underline">get in touch</ContactLink>.
                  </p>
                )}
              </section>

              <FormSection title="Goals" description="Optional, and for the whole school. They show as progress bars on the dashboard and donation pages." columns={2}>
                <MoneyField
                  name="money_goal_dollars"
                  label="Fundraising goal"
                  value={data.money_goal_dollars}
                  onChange={(e) => setData("money_goal_dollars", e.target.value)}
                  error={errors.money_goal_cents}
                />
                <FormField
                  name="minute_goal"
                  label="Reading goal (minutes)"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  placeholder="50000"
                  value={data.minute_goal}
                  onChange={(e) => setData("minute_goal", e.target.value)}
                  error={errors.minute_goal}
                />
              </FormSection>

              <div className="border-t border-border pt-5">
                <Button type="submit" size="lg" disabled={processing}>
                  {processing ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </SchoolLayout>
  );
}

function DateField({ name, label, value, onChange, error, locked }) {
  return (
    <FormField
      name={name}
      label={
        <span className="inline-flex items-center gap-1.5">
          {label}
          {locked && <Lock className="h-3 w-3 text-muted-foreground" aria-label="Fixed" />}
        </span>
      }
      type="date"
      required={!locked}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={error}
      disabled={locked}
    />
  );
}
